import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  getLocaleByUrlCode,
  normalizeLocale,
} from '../config/locales';

const KEY = 'shop-locale';
const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const { i18n } = useTranslation();
  const [locale, setLocaleState] = useState(() => {
    try {
      return normalizeLocale(localStorage.getItem(KEY) || DEFAULT_LOCALE);
    } catch {
      return DEFAULT_LOCALE;
    }
  });

  const setLocale = (next) => {
    const normalized = normalizeLocale(next);
    setLocaleState(normalized);
    try {
      localStorage.setItem(KEY, normalized);
    } catch {
      // ignore
    }
  };

  const current = useMemo(
    () => SUPPORTED_LOCALES.find((l) => l.code === locale) || SUPPORTED_LOCALES[0],
    [locale],
  );

  const getPathWithoutLocale = (pathname) => {
    const parts = String(pathname || '').split('/').filter(Boolean);
    if (parts.length && getLocaleByUrlCode(parts[0])) {
      const rest = parts.slice(1).join('/');
      return rest ? `/${rest}` : '/';
    }
    return pathname?.startsWith('/') ? pathname : `/${pathname || ''}`;
  };

  const value = useMemo(
    () => ({ locale, setLocale, locales: SUPPORTED_LOCALES, current, getPathWithoutLocale }),
    [locale, current],
  );

  useEffect(() => {
    if (i18n.language !== locale) {
      i18n.changeLanguage(locale);
    }
  }, [i18n, locale]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
}

